/**
 * abc.* attribute vocabulary — the single home for attribute-name constants,
 * value vocabularies, and the `setRouteAttributes` span helper.
 *
 * Inbound-gateway *values* (destination / outcome / platform allowlists) stay
 * in inbound-gateway/telemetry.ts. The attribute *names* live here.
 */
// Named imports (not `import * as`): the namespace form defeats tree-shaking
// and drags the whole SDK surface (replay/feedback) into the vendor chunk.
import {
	getActiveSpan as sentryGetActiveSpan,
	getClient as sentryGetClient,
	getRootSpan as sentryGetRootSpan,
	spanToJSON as sentrySpanToJSON,
} from '@sentry/sveltekit';

/**
 * Structural span shape this module touches. Not `@sentry/core`'s real `Span`
 * type: that type isn't reliably resolvable from this package under pnpm (see
 * the `SentryTracesSamplerContext` comment in config.ts), so this mirrors just
 * the members the route-root tracking below needs.
 */
type TelemetrySpan = {
	setAttributes?(attributes: Record<string, unknown>): void;
	isRecording?(): boolean;
};

/** Structural client shape route-root tracking needs (`Client#on`). */
type TelemetryClient = {
	on?(event: string, callback: (span: TelemetrySpan) => void): void;
};

/**
 * `LINK_FROM_APP_PLATFORM` vs. inbound-gateway's `abc.inbound_platform`
 * (telemetry.ts): two different platform attributes, both intentional.
 * `inbound_platform` is the bounded funnel-outcome attribute set only on the
 * gateway transaction (ios | android | samsung | other | none). This one is
 * the unbounded app-init diagnostic grouping key — it carries the raw,
 * unvalidated payload `platform` string as-is (e.g. 'ios' / 'android' /
 * 'samsung' / whatever a partner sends, never versioned), promoted from
 * `global.isLinkFromAppPlatform`. The versioned values (ios_1_0 / ios_2_0 /
 * android_1_0 / android_2_0) live on a different, similarly-named store
 * field — `global.linkFromAppPlatform` (no "is" prefix), set in the
 * inbound-gateway's `+page.js` — which is not promoted to any Sentry
 * attribute today. Do not merge these; pick the one matching what you're
 * slicing by.
 */
/** Canonical abc.* attribute names emitted by the route/app-init/experiment spans. */
export const ABC_ATTRIBUTES = {
	AUTH_COHORT: 'abc.auth_cohort',
	AUTH_OUTCOME: 'abc.auth_outcome',
	EXPERIMENT_COUNT: 'abc.experiment_count',
	EXPERIMENT_KEY: 'abc.experiment_key',
	EXPERIMENT_KEYS: 'abc.experiment_keys',
	/** Bulk init span only — count of keys in the Get payload (not assigned count on route root). */
	EXPERIMENT_REQUESTED_COUNT: 'abc.experiment_requested_count',
	IN_APP: 'abc.in_app',
	INBOUND_DESTINATION: 'abc.inbound_destination',
	INBOUND_ENTRY: 'abc.inbound_entry',
	INBOUND_OUTCOME: 'abc.inbound_outcome',
	INBOUND_PLATFORM: 'abc.inbound_platform',
	IS_FIRST_TIME_VISIT: 'abc.is_first_time_visit',
	LINK_FROM_APP: 'abc.link_from_app',
	LINK_FROM_APP_PLATFORM: 'abc.link_from_app_platform',
	NAV_FROM: 'abc.nav_from',
	NAV_INDEX: 'abc.nav_index',
	PAGE_INIT_RESULT: 'abc.page_init_result',
	PAGE_VARIANT: 'abc.page_variant',
	PAYMENT_METHODS_EXPECTED: 'abc.payment_methods_expected',
	PAYMENT_METHODS_READY: 'abc.payment_methods_ready',
	PRODUCT_EXPERIENCE: 'abc.product_experience',
	RENDERING_MODE: 'abc.rendering_mode',
	ROUTE_ID: 'abc.route_id',
	ROUTE_PATH: 'abc.route_path',
	SKELETON_VISIBLE: 'abc.skeleton_visible',
	VARIANT_PAINTED: 'abc.variant_painted',
	VARIANT_READY: 'abc.variant_ready',
} as const;

/** Outcome of `Page.init()`, recorded via `abc.page_init_result`. */
export const PAGE_INIT_RESULTS = {
	ERROR: 'error',
	RENDERED: 'rendered',
	// 'forbidden' — not 'unauthorized': Sentry's default scrubber redacts any
	// value containing "auth" (and Safe Fields do not reliably exempt span attrs).
	UNAUTHORIZED: 'forbidden',
} as const;

/** Rendering mode of a pageload/navigation span, recorded via `abc.rendering_mode`. */
export const RENDERING_MODES = {
	CSR_FALLBACK: 'csr_fallback',
	PRERENDERED: 'prerendered',
	SPA: 'spa',
} as const;

/**
 * Coerces a value to a queryable boolean string. Sentry Explore can't filter on
 * boolean attribute values, so booleans that need to be sliced in Explore must
 * be sent as the strings 'true'/'false' instead.
 * @param {unknown} value - Value to coerce.
 * @returns {'true' | 'false'} String form of the value's truthiness.
 */
export function toTelemetryBoolean(value: unknown): 'true' | 'false' {
	return value ? 'true' : 'false';
}

/** Assigned experiment row used for root-span `abc.experiment.<key>` attrs. */
type ExperimentTelemetryRow = {
	assigned?: unknown;
	key?: unknown;
	variant?: unknown;
};

/** Explore-legal suffix for `abc.experiment.<key>`. */
const EXPERIMENT_KEY_PATTERN = /^[A-Za-z0-9_.-]+$/;

/** Prefix for per-experiment root attributes (`abc.experiment.<key>` = variant). */
const EXPERIMENT_ATTR_PREFIX = 'abc.experiment.';

/**
 * Builds assigned-only experiment attributes for the route root.
 * Skips `assigned === false` (same filter as analytics `mapExperiments`).
 * Non-arrays (including the empty-object fixture) yield count 0.
 * @param {unknown} experiments - `app.global.experiments` (array of `{ key, variant, assigned }`).
 * @returns {Record<string, string | number>} Count plus one `abc.experiment.<key>` per assigned row.
 */
export function experimentRouteAttributes(experiments: unknown): Record<string, string | number> {
	const attrs: Record<string, string | number> = {
		[ABC_ATTRIBUTES.EXPERIMENT_COUNT]: 0,
	};
	if (!Array.isArray(experiments)) return attrs;

	let count = 0;
	for (const row of experiments) {
		if (!row || typeof row !== 'object') continue;
		const { assigned, key, variant } = row as ExperimentTelemetryRow;
		if (assigned === false) continue;
		if (typeof key !== 'string' || !EXPERIMENT_KEY_PATTERN.test(key)) continue;
		if (typeof variant !== 'string') continue;
		attrs[`${EXPERIMENT_ATTR_PREFIX}${key}`] = variant;
		count += 1;
	}
	attrs[ABC_ATTRIBUTES.EXPERIMENT_COUNT] = count;
	return attrs;
}

/**
 * Stamps assigned experiment arms onto the active transaction so Explore can
 * filter `abc.experiment.<key>`. Best-effort; never throws.
 * @param {unknown} experiments - `app.global.experiments`.
 * @returns {Record<string, string | number>} The attributes that were passed to `setRouteAttributes`.
 */
export function setExperimentRouteAttributes(
	experiments: unknown
): Record<string, string | number> {
	const attrs = experimentRouteAttributes(experiments);
	setRouteAttributes(attrs);
	return attrs;
}

/**
 * Attach bounded attributes to the active transaction. Silently drops null /
 * undefined / non-primitive values and never throws. Best-effort: when no
 * active span is reachable (tracing disabled, partially-mocked Sentry), the
 * call is a no-op.
 * @param {Record<string, string | number | boolean | null | undefined>} attributes - Bounded primitive attributes to attach.
 * @returns {void}
 */
export function setRouteAttributes(
	attributes: Record<string, string | number | boolean | null | undefined>
): void {
	if (!attributes || typeof attributes !== 'object') return;
	const span = getTransactionSpan();
	if (!span || typeof span.setAttributes !== 'function') return;
	try {
		const cleaned: Record<string, string | number> = {};
		for (const [key, value] of Object.entries(attributes)) {
			if (value == null) continue;
			// Booleans are coerced to 'true'/'false' here, at the choke point, so
			// every root attribute stays queryable in Sentry Explore (which can't
			// filter on boolean attribute values) — including legacy callers.
			if (typeof value === 'boolean') {
				cleaned[key] = toTelemetryBoolean(value);
			} else if (typeof value === 'string' || typeof value === 'number') {
				cleaned[key] = value;
			}
		}
		if (Object.keys(cleaned).length > 0) span.setAttributes(cleaned);
	} catch {
		/* attributes are best-effort */
	}
}

/* -------------------------------------------------------------------------- *
 * Route-root tracking — survives browser scope-stack loss
 *
 * `Sentry.getActiveSpan()` reads the span stamped on the CURRENT scope, and in
 * browsers (no AsyncLocalStorage) the "current scope" is the top of a global
 * stack (`@sentry/core` asyncContext/stackStrategy.js): `startSpan` pushes a
 * forked scope and pops it when its callback's promise settles. When a
 * navigation is triggered from INSIDE a traced async callback (historically
 * the inbound-gateway wrapped `goto()` in `instrument('inbound-gateway.redirect')`;
 * `goto()` now runs *after* that span ends, but any other `instrument()` that
 * still `await`s a navigation has the same shape), SvelteKit's `navigating`
 * SDK stamps the new navigation root span onto that callback's forked scope
 * (idleSpan.js `_startIdleSpan` -> `_setSpanForScope(getCurrentScope(), span)`).
 * The moment the wrapping callback settles, that scope is popped and the still-
 * running transaction becomes unreachable through `getActiveSpan()` — every
 * later `instrument()` span (`onlyIfParent` drops it), attribute, and
 * measurement silently vanishes. The tracker below keeps a per-client
 * reference to the latest pageload/navigation root span (captured via the
 * client's `spanStart` hook, which fires regardless of scope state) so those
 * late writes can fall back to the live transaction directly.
 * -------------------------------------------------------------------------- */

/** Root-span ops that own route telemetry (the idle pageload/navigation transactions). */
const ROUTE_ROOT_OPS = new Set(['pageload', 'navigation']);

/** Latest live route root span, keyed by client so tests with fresh clients never leak state. */
const routeRootSpanByClient = new WeakMap<object, TelemetrySpan>();

/** Clients whose `spanStart` tracking hook has been registered. */
const trackedClients = new WeakSet<object>();

/**
 * Registers the `spanStart` route-root tracking hook on the current client,
 * once per client. Safe to call on every helper entry; no-ops when the client
 * is unavailable (tracing disabled, partially-mocked Sentry).
 * @returns {TelemetryClient | undefined} The current client, when available.
 */
function ensureRouteRootTracking(): TelemetryClient | undefined {
	try {
		if (typeof sentryGetClient !== 'function') return undefined;
		const client = sentryGetClient() as TelemetryClient | undefined;
		if (!client || typeof client.on !== 'function') return client;
		if (!trackedClients.has(client)) {
			trackedClients.add(client);
			client.on('spanStart', (span) => {
				if (isRouteRootSpan(span)) routeRootSpanByClient.set(client, span);
			});
		}
		return client;
	} catch {
		return undefined; /* tracking is best-effort */
	}
}

/**
 * Whether `span` is its own root AND a pageload/navigation transaction. The op
 * filter matters: interaction idle spans (`ui.action.click`) are also roots,
 * and caching one would misdirect route telemetry onto a click transaction.
 * @param {TelemetrySpan} span - Span to classify.
 * @returns {boolean}
 */
function isRouteRootSpan(span: TelemetrySpan): boolean {
	if (typeof sentryGetRootSpan !== 'function' || typeof sentrySpanToJSON !== 'function') {
		return false;
	}
	try {
		if ((sentryGetRootSpan(span as never) as TelemetrySpan) !== span) return false;
		const op = (sentrySpanToJSON(span as never) as { op?: string } | undefined)?.op;
		return typeof op === 'string' && ROUTE_ROOT_OPS.has(op);
	} catch {
		return false;
	}
}

/**
 * Whether the span is still recording (not ended). Ended roots must never be
 * used as a fallback: writes to them are silently dropped, and a stale root
 * from a previous route would swallow the next route's telemetry.
 * @param {TelemetrySpan | undefined} span - Span to check.
 * @returns {boolean}
 */
function isRecordingSpan(span: TelemetrySpan | undefined): boolean {
	if (!span || typeof span.isRecording !== 'function') return false;
	try {
		return span.isRecording() === true;
	} catch {
		return false;
	}
}

/**
 * The live route transaction to use when `getActiveSpan()` resolves nothing —
 * i.e. after the scope stack lost the navigation root (see the module comment
 * above). Returns undefined whenever an active span IS resolvable (normal
 * operation: callers must keep the SDK's own resolution) or when no tracked
 * root is still recording (nothing live to attach to — dropping is correct).
 * @returns {TelemetrySpan | undefined}
 */
export function getFallbackRootSpan(): TelemetrySpan | undefined {
	const client = ensureRouteRootTracking();
	if (getActiveSpan()) return undefined;
	if (!client) return undefined;
	const cached = routeRootSpanByClient.get(client);
	return isRecordingSpan(cached) ? cached : undefined;
}

/**
 * Returns the root span (transaction) for the currently-active span. Attributes
 * must land on the transaction: the SDK only serializes the root span's
 * attributes into `contexts.trace.data`, so anything attached to a child span
 * (e.g. the `function.sveltekit.load` span wrapping every `load`) is invisible
 * to transaction-level queries. `Sentry.setMeasurement` already resolves the
 * root internally; this keeps attributes consistent with it. When no span is
 * active at all, falls back to the tracked route root (scope-stack loss, see
 * the route-root tracking comment above).
 * @returns {TelemetrySpan | undefined}
 */
function getTransactionSpan(): TelemetrySpan | undefined {
	const client = ensureRouteRootTracking();
	const active = getActiveSpan();
	if (!active) {
		if (!client) return undefined;
		const cached = routeRootSpanByClient.get(client);
		return isRecordingSpan(cached) ? cached : undefined;
	}
	let root = active;
	if (typeof sentryGetRootSpan === 'function') {
		try {
			root = (sentryGetRootSpan(active as never) as TelemetrySpan) ?? active;
		} catch {
			root = active;
		}
	}
	// Opportunistic seed: roots that started before the spanStart hook was
	// registered (e.g. the initial pageload span, created during Sentry.init)
	// are captured here on their first successful resolution instead.
	if (client && isRecordingSpan(root) && isRouteRootSpan(root)) {
		routeRootSpanByClient.set(client, root);
	}
	return root;
}

/**
 * Returns the currently-active Sentry span/transaction, or undefined when
 * tracing is unavailable. SDK v10 removed `getCurrentHub`, so this only tries
 * the v8+ `getActiveSpan` API — no hub/scope fallback is reachable anymore.
 * @returns {TelemetrySpan | undefined}
 */
function getActiveSpan(): TelemetrySpan | undefined {
	try {
		if (typeof sentryGetActiveSpan !== 'function') return undefined;
		return (sentryGetActiveSpan() as TelemetrySpan) ?? undefined;
	} catch {
		return undefined;
	}
}
